import { createContext, useCallback, useContext, useRef, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { useT } from "@/lib/i18n";

/**
 * App-wide confirm dialog that replaces browser-native `window.confirm`.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title, body, confirmLabel, tone: 'danger' }))) return;
 *
 * A single dialog instance lives at the root so callers never need to manage
 * open/close state. Resolves `true` on confirm, `false` on cancel or backdrop
 * click. Tone `danger` uses the rose accent for destructive actions.
 */
const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const { t } = useT();
  const [state, setState] = useState(null); // { title, body, confirmLabel, cancelLabel, tone }
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    // Accept a plain string as shorthand for a body-only prompt.
    const payload = typeof opts === "string" ? { body: opts } : (opts || {});
    setState(payload);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = (value) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    resolver?.(value);
  };

  const open = !!state;
  const tone = state?.tone || "danger";
  const isDanger = tone === "danger";

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {open && (
        <div className="fixed inset-0 z-[100]" data-testid="confirm-dialog">
          <div
            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
            onClick={() => settle(false)}
          />
          <div className="relative z-10 mx-auto mt-32 max-w-md bg-[#F9F8F6] border border-neutral-900 shadow-2xl">
            <div className="px-6 pt-6 pb-4 flex items-start gap-4">
              <div className={`shrink-0 border p-2.5 ${isDanger ? "border-rose-700 text-rose-700 bg-rose-50" : "border-neutral-900 text-neutral-900 bg-white"}`}>
                <AlertTriangle size={20} strokeWidth={1.75} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-serif text-2xl leading-tight" data-testid="confirm-title">
                  {state.title || t("confirmTitle")}
                </h2>
                {state.body && (
                  <p className="text-sm text-neutral-700 mt-2 leading-relaxed" data-testid="confirm-body">
                    {state.body}
                  </p>
                )}
              </div>
              <button
                data-testid="confirm-close"
                onClick={() => settle(false)}
                className="p-2 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>
            <div className="border-t border-neutral-200 px-6 py-4 flex items-center justify-end gap-3">
              <button
                data-testid="confirm-cancel"
                onClick={() => settle(false)}
                className="ed-btn ed-btn-outline"
                autoFocus
              >
                {state.cancelLabel || t("confirmCancel")}
              </button>
              <button
                data-testid="confirm-confirm"
                onClick={() => settle(true)}
                className={isDanger ? "ed-btn-danger" : "ed-btn"}
              >
                {state.confirmLabel || t("confirmConfirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

/** Returns an async confirm(msg | opts) function. */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside a ConfirmProvider");
  return ctx;
}
